import { PropType } from 'vue'

import {
  BREAKPOINT_MODE_OPTIONS,
  DEFAULT_CONFIG,
  DIR_MAP,
  DIR_OPTIONS,
  SLIDE_EFFECTS,
  SNAP_ALIGN_OPTIONS,
} from '@/shared'

import type {
  BreakpointMode,
  CarouselConfig,
  Dir,
  DragConfig,
  NonNormalizedDir,
  NormalizedDir,
  SlideEffect,
  SnapAlign,
  TransitionEasing,
  WheelConfig,
} from '@/shared'

export const isValidClassPrefix = (value: unknown): value is string =>
  typeof value === 'string' && /^\S+$/.test(value)

export const carouselProps = {
  // follow the height of the visible slides instead of the tallest slide
  adaptiveHeight: {
    default: DEFAULT_CONFIG.adaptiveHeight,
    type: Boolean,
    validator(value: boolean, props: { dir?: Dir }) {
      const dir = props.dir || DEFAULT_CONFIG.dir!
      const normalizedDir =
        dir in DIR_MAP ? DIR_MAP[dir as NonNormalizedDir] : (dir as NormalizedDir)
      if (value && ['ttb', 'btt'].includes(normalizedDir)) {
        console.warn(
          `[vue3-carousel]: "adaptiveHeight" is ignored with the vertical dir "${dir}".`
        )
      }
      return true
    },
  },
  // time to auto advance slides in ms
  autoplay: {
    default: DEFAULT_CONFIG.autoplay,
    type: Number,
  },
  // slide to a slide when it, or an element inside it, receives keyboard or programmatic focus
  autoScrollOnFocus: {
    default: DEFAULT_CONFIG.autoScrollOnFocus,
    type: Boolean,
  },
  // an object to store breakpoints
  breakpoints: {
    default: DEFAULT_CONFIG.breakpoints,
    type: Object as PropType<CarouselConfig['breakpoints']>,
  },
  // controls the breakpoint mode relative to the carousel container or the viewport
  breakpointMode: {
    default: DEFAULT_CONFIG.breakpointMode,
    validator(value: BreakpointMode) {
      const isValid = BREAKPOINT_MODE_OPTIONS.includes(value)
      if (!isValid) {
        console.warn(`[vue3-carousel]: Invalid breakpointMode "${value}". Allowed values: ${BREAKPOINT_MODE_OPTIONS.join(', ')}`)
      }
      return isValid
    },
  },
  clamp: {
    type: Boolean,
  },
  // prefix for every CSS class the carousel renders
  classPrefix: {
    default: DEFAULT_CONFIG.classPrefix,
    type: String,
    validator(value: string) {
      const isValid = isValidClassPrefix(value)
      if (!isValid) {
        console.warn(
          `[vue3-carousel]: Invalid classPrefix "${value}". It must be a non-empty string without whitespace.`
        )
      }
      return isValid
    },
  },
  // control the direction of the carousel
  dir: {
    type: String as PropType<Dir>,
    default: DEFAULT_CONFIG.dir,
    validator(value: Dir, props: { height?: string }) {
      if (!DIR_OPTIONS.includes(value)) {
        console.warn(`[vue3-carousel]: Invalid dir "${value}". Allowed values: ${DIR_OPTIONS.join(', ')}`)
        return false
      }
      const normalizedDir = value in DIR_MAP ? DIR_MAP[value as NonNormalizedDir] : (value as NormalizedDir)
      if (["ttb", "btt"].includes(normalizedDir) && (!props.height || props.height === "auto")) {
        console.warn(`[vue3-carousel]: The dir "${value}" is not supported with height "auto".`)
      }
      return true
    },
  },
  // lock the carousel and hide its addons while every slide fits in the viewport
  disableWhenSlidesFit: {
    default: DEFAULT_CONFIG.disableWhenSlidesFit,
    type: Boolean,
  },
  // space in pixels before the first slide and after the last slide
  edgeSpacing: {
    default: DEFAULT_CONFIG.edgeSpacing,
    type: Number,
    validator(value: number) {
      const isValid = value >= 0
      if (!isValid) {
        console.warn(
          `[vue3-carousel]: Invalid edgeSpacing "${value}". It must be 0 or greater.`
        )
      }
      return isValid
    },
  },
  // enable/disable the carousel component
  enabled: {
    default: DEFAULT_CONFIG.enabled,
    type: Boolean,
  },
  // control the gap between slides
  gap: {
    default: DEFAULT_CONFIG.gap,
    type: Number,
  },
  // set carousel height
  height: {
    default: DEFAULT_CONFIG.height,
    type: [Number, String],
  },
  // aria-labels and additional text labels
  i18n: {
    default: DEFAULT_CONFIG.i18n,
    type: Object as PropType<typeof DEFAULT_CONFIG.i18n>,
  },
  ignoreAnimations: {
    default: false,
    type: [Array, Boolean, String] as PropType<CarouselConfig['ignoreAnimations']>,
  },
  // count of items to be scrolled
  itemsToScroll: {
    default: DEFAULT_CONFIG.itemsToScroll,
    type: Number,
  },
  // count of items to showed per view
  itemsToShow: {
    default: DEFAULT_CONFIG.itemsToShow,
    type: [Number, String],
  },
  // toggle arrow-key navigation while the carousel is focused
  keyboardNavigation: {
    default: DEFAULT_CONFIG.keyboardNavigation,
    type: Boolean,
  },
  // continuously scroll the slides at a constant speed
  marquee: {
    default: DEFAULT_CONFIG.marquee,
    type: Boolean,
    validator(value: boolean, props: { slideEffect?: SlideEffect }) {
      if (value && props.slideEffect === 'fade') {
        console.warn(
          `[vue3-carousel]: "marquee" cannot be used with slideEffect "fade". The setting will be ignored.`
        )
      }
      return true
    },
  },
  // marquee speed in pixels per second
  marqueeSpeed: {
    default: DEFAULT_CONFIG.marqueeSpeed,
    type: Number,
    validator(value: number) {
      const isValid = value > 0
      if (!isValid) {
        console.warn(
          `[vue3-carousel]: Invalid marqueeSpeed "${value}". It must be greater than 0.`
        )
      }
      return isValid
    },
  },
  // slide number number of initial slide
  modelValue: {
    default: undefined,
    type: Number,
  },
  // toggle mouse dragging
  mouseDrag: {
    default: DEFAULT_CONFIG.mouseDrag,
    type: [Boolean, Object] as PropType<boolean | DragConfig>,
  },
  // toggle mouse wheel scrolling
  mouseWheel: {
    default: DEFAULT_CONFIG.mouseWheel,
    type: [Boolean, Object] as PropType<boolean | WheelConfig>,
  },
  // control mouse scroll threshold
  mouseScrollThreshold: {
    default: DEFAULT_CONFIG.mouseScrollThreshold,
    type: Number,
  },
  // render a CSS scroll-snap carousel instead of a transformed track
  nativeCss: {
    default: DEFAULT_CONFIG.nativeCss,
    type: Boolean,
  },
  pauseAutoplayOnHover: {
    default: DEFAULT_CONFIG.pauseAutoplayOnHover,
    type: Boolean,
  },
  preventExcessiveDragging: {
    default: false,
    type: Boolean,
    validator(value: boolean, props: { wrapAround?: boolean }) {
      if (value && props.wrapAround) {
        console.warn(
          `[vue3-carousel]: "preventExcessiveDragging" cannot be used with wrapAround. The setting will be ignored.`
        )
      }
      return true
    },
  },
  slideEffect: {
    type: String as PropType<SlideEffect>,
    default: DEFAULT_CONFIG.slideEffect,
    validator(value: SlideEffect) {
      const isValid = SLIDE_EFFECTS.includes(value)
      if (!isValid) {
        console.warn(`[vue3-carousel]: Invalid slideEffect "${value}". Allowed values: ${SLIDE_EFFECTS.join(', ')}`)
      }
      return isValid
    },
  },
  // control snap position alignment
  snapAlign: {
    default: DEFAULT_CONFIG.snapAlign,
    validator(value: SnapAlign) {
      const isValid = SNAP_ALIGN_OPTIONS.includes(value)
      if (!isValid) {
        console.warn(`[vue3-carousel]: Invalid snapAlign "${value}". Allowed values: ${SNAP_ALIGN_OPTIONS.join(', ')}`)
      }
      return isValid
    },
  },
  // toggle touch dragging
  touchDrag: {
    default: DEFAULT_CONFIG.touchDrag,
    type: [Boolean, Object] as PropType<boolean | DragConfig>,
  },
  // sliding transition time in ms
  transition: {
    default: DEFAULT_CONFIG.transition,
    type: Number,
  },
  // transition easing function
  transitionEasing: {
    default: DEFAULT_CONFIG.transitionEasing,
    type: String as PropType<TransitionEasing>,
  },
  // control infinite scrolling mode
  wrapAround: {
    default: DEFAULT_CONFIG.wrapAround,
    type: Boolean,
  },
}
